import { useDispatch, useSelector } from "react-redux";
import {
    setConfigurations,
    setActiveConfiguration,
    updateConfigurationFormField,
    setLoading,
    setSubmitting,
    setTestResult,
    setCreateModalOpen,
    setEditModalOpen,
    setSelectedConfiguration,
    resetConfigurationForm,
    setTestModalOpen,
} from "../../store/slices/qztray/qzTrayConfigurationSlice";

import { RootState } from "../../store/store";
import {
    QzTrayConfiguration,
    QzTrayConfigurationForm,
} from "../../types/qztray/QzTrayConfigurationTypes";

import useApiErrorHandler from "../useApiErrorHandler";
import useLoading from "../useLoading";
import useAlerts from "../useAlerts";
import useModals from "../useModals";
import QzTrayConfigurationService from "../../services/qztray/qzTrayConfigurationService";

/**
 * ⚙️ Hook: useQzTrayConfiguration
 *
 * Gestiona la lógica del módulo QZ Tray Configuration:
 * listar, guardar, actualizar, probar conexión y manejar modales.
 */
export const useQzTrayConfiguration = () => {
    const dispatch = useDispatch();
    const {
        configurations,
        activeConfiguration,
        form,
        testResult,
        isLoading,
        isSubmitting,
        error,
        createModalOpen,
        editModalOpen,
        testModalOpen,
        selectedConfiguration,
    } = useSelector((state: RootState) => state.qzTrayConfiguration);

    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { show: showAlert } = useAlerts();
    const { openModal } = useModals();

    // ========================================================
    // 📋 Obtener todas las configuraciones
    // ========================================================
    const fetchConfigurations = async () => {
        try {
            dispatch(setLoading(true));
            showLoadingIndicator();

            const response = await QzTrayConfigurationService.getAllConfigurations();
            const { statusCode, data } = response;

            if (statusCode === 200) {
                dispatch(setConfigurations(data));
            } else {
                showAlert("warning", "Atención", "No se encontraron configuraciones registradas.");
            }
        } catch (error: any) {
            handle(error);
        } finally {
            dispatch(setLoading(false));
            hideLoadingIndicator();
        }
    };

    // ========================================================
    // 🔍 Obtener configuración activa por tenant
    // ========================================================
    const fetchActiveConfiguration = async (tenantId: string) => {
        try {
            dispatch(setLoading(true));
            showLoadingIndicator();

            const response = await QzTrayConfigurationService.getActiveConfiguration(tenantId);
            console.log(response);
            const { statusCode, data } = response;

            if (statusCode === 200) {
                dispatch(setActiveConfiguration(data));
            } else {
                showAlert("info", "Sin configuración activa", "Este tenant aún no tiene configuración.");
            }
        } catch (error: any) {
            handle(error);
        } finally {
            dispatch(setLoading(false));
            hideLoadingIndicator();
        }
    };

    // ========================================================
    // 💾 Guardar o actualizar configuración
    // ========================================================
    const saveOrUpdateConfiguration = async (formData: QzTrayConfigurationForm) => {
        try {
            dispatch(setSubmitting(true));
            showLoadingIndicator();

            const response = await QzTrayConfigurationService.saveOrUpdateConfiguration(formData);
            const { statusCode, data, title, message, type } = response;

            if (statusCode === 200) {
                dispatch(setActiveConfiguration(data));
                fetchConfigurations();
                openModal(type, { title, message });
                closeCreateModal();
                closeEditModal();
            } else {
                showAlert("error", "Error", "No se pudo guardar la configuración.");
            }
        } catch (error: any) {
            handle(error);
        } finally {
            dispatch(setSubmitting(false));
            hideLoadingIndicator();
        }
    };

    // ========================================================
    // 🧪 Probar conexión QZ Tray
    // ========================================================
    const testConnection = async (formData: QzTrayConfigurationForm) => {
        try {
            dispatch(setSubmitting(true));
            showLoadingIndicator();
            const response = await QzTrayConfigurationService.testConnection(formData);
            const { statusCode, data } = response;
            if (statusCode === 200 || statusCode === 503) {
                dispatch(setTestResult(data));
                openTestModal();
            }
        } catch (error: any) {
            handle(error);
        } finally {
            dispatch(setSubmitting(false));
            hideLoadingIndicator();
        }
    };

    // ========================================================
    // 🧭 Control de campos y modales
    // ========================================================
    const updateField = <K extends keyof QzTrayConfigurationForm>(
        field: K,
        value: QzTrayConfigurationForm[K]
    ) => {
        dispatch(updateConfigurationFormField({ field, value }));
    };

    const openCreateModal = () => dispatch(setCreateModalOpen(true));
    const closeCreateModal = () => dispatch(setCreateModalOpen(false));

    const openEditModal = () => dispatch(setEditModalOpen(true));
    const closeEditModal = () => dispatch(setEditModalOpen(false));

    const openTestModal = () => dispatch(setTestModalOpen(true));
    const closeTestModal = () => dispatch(setTestModalOpen(false));


    const selectConfigurationToEdit = (config: QzTrayConfiguration) => {
        dispatch(setSelectedConfiguration(config));
        openEditModal();
    };

    const clearForm = () => {
        dispatch(resetConfigurationForm());
    };

    // ========================================================
    // 🧩 Retorno del hook
    // ========================================================
    return {
        configurations,
        activeConfiguration,
        form,
        testResult,
        isLoading,
        isSubmitting,
        error,
        createModalOpen,
        editModalOpen,
        testModalOpen,
        selectedConfiguration,

        fetchConfigurations,
        fetchActiveConfiguration,
        saveOrUpdateConfiguration,
        testConnection,
        updateField,

        openCreateModal,
        closeCreateModal,
        openEditModal,
        closeEditModal,
        openTestModal,
        closeTestModal,
        selectConfigurationToEdit,
        clearForm,
    };
};
